import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import DatePicker from "react-datepicker";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { getIntlLocale } from "../../../utils/functions";
import { getReactDatepickerLocale } from "../../../utils/dateLocales";
import FieldShell, {
  fieldSurface,
  FOCUS_RING_ONLY,
  SELECTED_TINT,
  FIELD_VALUE_CLASS,
  FIELD_PLACEHOLDER_CLASS,
} from "./FieldShell";
import { cn } from "./Input";

/** Gap between the field and the popover, and the viewport safe margin. */
/**
 * The hour a deadline means when nobody says otherwise.
 *
 * Picking a date used to silently stamp local midnight, which reads as "the
 * very start of that day" — so a task "due Friday" was already overdue at
 * 00:01 on Friday morning. End of the business day is what people mean, so a
 * fresh time-carrying picker starts here instead.
 *
 * Only a picker that SHOWS the time control gets this: date-only pickers
 * (billing rates, time-entry dates, filter ranges) still mean plain midnight,
 * and a date that already carries a time keeps it — existing 00:00 rows are
 * never rewritten behind the user's back.
 */
export const DEFAULT_DEADLINE_HOUR = 17;
export const DEFAULT_DEADLINE_MINUTE = 0;

const MENU_GAP = 6;
const VIEWPORT_MARGIN = 8;
/** First-frame guess only — the real height is measured from the panel itself. */
const ESTIMATED_PANEL_HEIGHT = 340;

interface DateTimePickerProps {
  onChange: (date: Date | null) => void;
  id?: string;
  label?: string;
  selected?: Date | null;
  placeholder?: string;
  required?: boolean;
  error?: string | boolean;
  helperText?: string;
  showTimeSelect?: boolean;
  timeIntervals?: number;
  dateFormat?: string;
  className?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  fullWidth?: boolean;
  /**
   * Filter-bar density — the same request `Dropdown` takes, so a date filter
   * standing beside a dropdown filter is the same height as it.
   */
  thinner?: boolean;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  id,
  label,
  selected,
  onChange,
  placeholder,
  required,
  error,
  helperText,
  showTimeSelect = true,
  timeIntervals = 15,
  dateFormat = "MMM d, yyyy h:mm aa",
  className,
  disabled,
  minDate,
  maxDate,
  fullWidth = false,
  thinner = false,
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language; // Triggers re-render on language change
  const [isOpen, setIsOpen] = useState(false);
  /** The time an untouched picker starts at — see DEFAULT_DEADLINE_HOUR. */
  const defaultTime = showTimeSelect
    ? { hours: DEFAULT_DEADLINE_HOUR, minutes: DEFAULT_DEADLINE_MINUTE }
    : { hours: 0, minutes: 0 };
  const [workingDate, setWorkingDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<{ hours: number; minutes: number } | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0, maxHeight: 0 });
  const [shouldDropUp, setShouldDropUp] = useState(false);
  const portalRef = useRef<HTMLDivElement | null>(null);

  // Initialize working state when popover opens or selected date changes
  useEffect(() => {
    if (isOpen) {
      if (selected) {
        setWorkingDate(new Date(selected));
        setSelectedTime({
          hours: selected.getHours(),
          minutes: Math.floor(selected.getMinutes() / timeIntervals) * timeIntervals,
        });
      } else {
        setWorkingDate(null);
        setSelectedTime(defaultTime);
      }
    }
    // `defaultTime` is derived from `showTimeSelect`, which is in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selected, timeIntervals, showTimeSelect]);

  const formatDateDisplay = useCallback(() => {
    if (!selected) return placeholder || t("Select date");
    // Locale-aware formatting. Omit hour12 — Intl picks the locale's default
    // (en-US 12h, sv/de/nb/da/fi/nl/he/es 24h).
    const formatter = new Intl.DateTimeFormat(getIntlLocale(), {
      month: "short",
      day: "numeric",
      year: "numeric",
      ...(showTimeSelect && { hour: "numeric", minute: "2-digit" }),
    });
    return formatter.format(selected);
    // language is a dep so the display refreshes when the user switches language
  }, [selected, showTimeSelect, placeholder, t, language]);

  const updateMenuPosition = useCallback(() => {
    if (pickerRef.current && isOpen) {
      const rect = pickerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

      // Measure the panel once it exists; the estimate only covers the first
      // frame. The old code assumed a flat 400px and flipped to `rect.top - 400`,
      // which threw the panel to the top of the screen whenever it was shorter.
      const measuredHeight = portalRef.current?.offsetHeight || 0;
      const panelHeight = measuredHeight || ESTIMATED_PANEL_HEIGHT;

      const spaceBelow = viewportHeight - rect.bottom - VIEWPORT_MARGIN;
      const spaceAbove = rect.top - VIEWPORT_MARGIN;
      // Flip only when there genuinely isn't room below AND above is roomier.
      const shouldFlip = spaceBelow < panelHeight + MENU_GAP && spaceAbove > spaceBelow;
      setShouldDropUp(shouldFlip);

      // Never taller than the side we ended up on — it scrolls instead of
      // hanging off the viewport.
      const available = Math.max(180, (shouldFlip ? spaceAbove : spaceBelow) - MENU_GAP);
      const height = Math.min(panelHeight, available);

      // Ensure enough width for calendar + time picker side-by-side
      const minWidth = showTimeSelect ? 420 : rect.width;
      const portalWidth = Math.max(rect.width, minWidth);
      // Clamp left so the picker doesn't overflow the viewport edge
      const left = Math.max(8, Math.min(rect.left, viewportWidth - portalWidth - 16));

      const top = shouldFlip
        ? Math.max(VIEWPORT_MARGIN, rect.top - height - MENU_GAP)
        : Math.min(rect.bottom + MENU_GAP, viewportHeight - height - VIEWPORT_MARGIN);

      setMenuPosition({ top, left, width: portalWidth, maxHeight: available });
    }
  }, [isOpen, showTimeSelect]);

  // Reposition once the panel is actually in the DOM, so the first paint uses a
  // measured height rather than the estimate.
  useLayoutEffect(() => {
    if (isOpen) updateMenuPosition();
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (isOpen) {
      updateMenuPosition();
      const handleScroll = () => updateMenuPosition();
      const handleResize = () => updateMenuPosition();

      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is within picker header OR within portal content
      const isClickInPickerHeader = pickerRef.current && pickerRef.current.contains(target);
      const isClickInPortal = portalRef.current && portalRef.current.contains(target);

      // Only close if click is outside both the header and the portal
      if (!isClickInPickerHeader && !isClickInPortal) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleTimeChange = (hours?: number, minutes?: number) => {
    const newTime = {
      hours: hours !== undefined ? hours : (selectedTime?.hours ?? defaultTime.hours),
      minutes: minutes !== undefined ? minutes : (selectedTime?.minutes ?? defaultTime.minutes),
    };
    setSelectedTime(newTime);

    // Update working date with new time
    if (workingDate) {
      const updatedDate = new Date(workingDate);
      updatedDate.setHours(newTime.hours, newTime.minutes, 0);
      setWorkingDate(updatedDate);
    }
  };

  const handleDateSelect = (date: Date | null) => {
    if (date) {
      // Update working date with the selected date
      const currentTime = selectedTime || defaultTime;
      date.setHours(currentTime.hours, currentTime.minutes, 0);
      setWorkingDate(date);

      // Only close if time select is not shown (date-only picker)
      if (!showTimeSelect) {
        onChange(date);
        setIsOpen(false);
      }
      // If time select is shown, keep popover open and wait for user to select time and confirm
    }
  };

  const handleConfirm = () => {
    if (workingDate) {
      onChange(workingDate);
      setIsOpen(false);
    }
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 / timeIntervals }, (_, i) => i * timeIntervals);

  return (
    <FieldShell
      id={id}
      label={label}
      required={required}
      error={error && helperText ? helperText : undefined}
      className="flex flex-col"
    >
      <div className="relative" ref={pickerRef}>
        <div
          className={cn(
            fieldSurface({ error: !!error, disabled, ...(thinner ? { height: false as const } : {}) }),
            "cursor-pointer flex justify-between items-center px-3",
            thinner ? "py-0.5" : "py-1.5",
            className
          )}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (!disabled && (e.key === "Enter" || e.key === " ")) {
              setIsOpen(!isOpen);
            }
          }}
        >
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-fg-muted flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span className={clsx('truncate', selected ? FIELD_VALUE_CLASS : FIELD_PLACEHOLDER_CLASS)}>
              {formatDateDisplay()}
            </span>
          </div>
          <svg
            className={clsx(
              "text-fg-muted w-4 h-4 flex-shrink-0 transition-transform",
              isOpen && "rotate-180"
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {isOpen &&
          createPortal(
            <motion.div
              ref={portalRef}
              initial={{ opacity: 0, y: shouldDropUp ? 10 : -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: shouldDropUp ? 10 : -10, scale: 0.95 }}
              /* The brand `ease-pop` curve, as framer-motion's bezier form. */
              transition={{ duration: 0.16, ease: [0.4, 0, 0.3, 1] }}
              className="glass rounded-lg shadow-sm z-[300000001]"
              style={{
                position: "fixed",
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
                width: `${menuPosition.width}px`,
                ...(menuPosition.maxHeight
                  ? { maxHeight: `${menuPosition.maxHeight}px`, overflowY: "auto" as const }
                  : {}),
              }}
            >
              <div className="p-4 flex gap-4">
                {/* Calendar Section */}
                <div>
                  <DatePicker
                    selected={workingDate}
                    onChange={handleDateSelect}
                    inline
                    minDate={minDate}
                    maxDate={maxDate}
                    locale={getReactDatepickerLocale()}
                    wrapperClassName="react-datepicker-wrapper-inline"
                    calendarClassName="!bg-transparent dark:text-white"
                  />
                </div>

                {/* Time Picker Section */}
                {showTimeSelect && (
                  <div className="flex flex-col gap-4 pt-2">
                    <div className="text-xs text-fg-body">
                      {t("Time")}
                    </div>

                    {/* Hours */}
                    <div className="flex flex-col gap-2">
                      <label className="text-xs text-fg-muted">{t("Hours")}</label>
                      <div className="max-h-40 overflow-y-auto border border-hairline rounded">
                        {hours.map((hour) => (
                          <button
                            key={`hour-${hour}`}
                            type="button"
                            className={clsx(
                              "w-12 px-3 py-1.5 text-xs border-b border-hairline-soft transition-colors",
                              // Soft tint, not a saturated fill: a column of
                              // solid champagne cells shouted louder than the
                              // date being picked.
                              selectedTime?.hours === hour
                                ? `${SELECTED_TINT} font-medium`
                                : "bg-transparent text-fg-body hover:bg-surface-soft"
                            )}
                            onClick={() => handleTimeChange(hour, undefined)}
                          >
                            {String(hour).padStart(2, "0")}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Minutes */}
                    <div className="flex flex-col gap-2">
                      <label className="text-xs text-fg-muted">{t("Minutes")}</label>
                      <div className="max-h-40 overflow-y-auto border border-hairline rounded">
                        {minutes.map((minute) => (
                          <button
                            key={`minute-${minute}`}
                            type="button"
                            className={clsx(
                              "w-12 px-3 py-1.5 text-xs border-b border-hairline-soft transition-colors",
                              selectedTime?.minutes === minute
                                ? `${SELECTED_TINT} font-medium`
                                : "bg-transparent text-fg-body hover:bg-surface-soft"
                            )}
                            onClick={() => handleTimeChange(undefined, minute)}
                          >
                            {String(minute).padStart(2, "0")}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm/Close Button */}
              <div className="border-t border-hairline p-3 flex justify-end gap-2">
                <button
                  type="button"
                  className={clsx("px-3 py-1.5 text-xs bg-surface-sunken/70 text-fg-body rounded hover:bg-surface-sunken transition-colors", FOCUS_RING_ONLY)}
                  onClick={() => {
                    if (showTimeSelect) {
                      handleConfirm();
                    } else {
                      setIsOpen(false);
                    }
                  }}
                >
                  {t(showTimeSelect ? "Confirm" : "Close")}
                </button>
              </div>
            </motion.div>,
            document.body
          )}
      </div>
    </FieldShell>
  );
};

export default DateTimePicker;
